'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectIsAuthenticated } from '@repo/store/maps/slices/auth';
import { selectExternalMapId, selectLocationEnabled, selectPlayerLocationDeltas, selectTitleId, selectTitleName, selectTitleSlug } from '@repo/store/maps/slices/mapData';
import type { LiveLocationCtaSource } from '@repo/types/analytic-events/maps-analytic-events';
import { HostMode, HostType } from '@repo/types/host';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMapContext } from '../game-map/map-context';
import type { LiveLocationCtaControl } from '../map-controls/player-location-button';
import { useLiveLocationCtaSuppression } from './use-live-location-cta-suppression';
interface UseLiveLocationCtaOptions {
  clearSelectedMarkerInMap: () => void;
  isMiniMapMode: boolean;
  isMobileViewport: boolean;
  isSessionMiniMap: boolean;
  selectedLocationId?: string | null;
}
export function useLiveLocationCta({
  clearSelectedMarkerInMap,
  isMiniMapMode,
  isMobileViewport,
  isSessionMiniMap,
  selectedLocationId
}: UseLiveLocationCtaOptions) {
  const analytics = useAnalytics();
  const hostType = useHostType();
  const hostMode = useHostMode();
  const {
    map
  } = useMapContext();
  const externalMapId = useAppSelector(selectExternalMapId);
  const titleId = useAppSelector(selectTitleId);
  const titleName = useAppSelector(selectTitleName);
  const titleSlug = useAppSelector(selectTitleSlug);
  const playerLocationDeltas = useAppSelector(selectPlayerLocationDeltas);
  const locationEnabled = useAppSelector(selectLocationEnabled);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isEligible = hostType === HostType.WEB && hostMode !== HostMode.EMBED && !isMobileViewport && !isMiniMapMode && !isSessionMiniMap && !locationEnabled && Boolean(playerLocationDeltas);
  const suppressionKey = titleSlug ?? titleId ?? externalMapId ?? null;
  const {
    isSuppressed,
    markSeen
  } = useLiveLocationCtaSuppression(suppressionKey);
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const wasOpenRef = useRef(false);
  const [source, setSource] = useState<LiveLocationCtaSource>('locate_button');
  const showMarker = isEligible && !isSuppressed && !isOpen && !isDismissed;
  const analyticsParams = useMemo(() => ({
    mapId: externalMapId,
    titleId,
    titleName,
    titleSlug,
    platform: hostType,
    isAuthenticated
  }), [externalMapId, hostType, isAuthenticated, titleId, titleName, titleSlug]);
  const analyticsParamsRef = useRef(analyticsParams);
  analyticsParamsRef.current = analyticsParams;
  const close = useCallback(() => setIsOpen(false), []);
  useEffect(() => {
    if (!isEligible) {
      close();
    }
  }, [close, isEligible]);
  useEffect(() => {
    if (selectedLocationId) {
      close();
    }
  }, [close, selectedLocationId]);
  useEffect(() => {
    if (!map || !isEligible) return;
    map.on('click', close);
    return () => {
      map.off('click', close);
    };
  }, [close, isEligible, map]);
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current) {
      setIsDismissed(true);
    }
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset dismissal when the title/map changes.
  useEffect(() => {
    wasOpenRef.current = false;
    setIsDismissed(false);
  }, [suppressionKey]);
  useEffect(() => {
    if (!isOpen || !isEligible) return;
    analytics.event('game_map_live_location_cta_impression', {
      ...analyticsParamsRef.current,
      source
    });
  }, [analytics, isEligible, isOpen, source]);
  const handleHover = useCallback((ctaSource: LiveLocationCtaSource) => {
    analytics.event('game_map_live_location_cta_trigger_hover', {
      ...analyticsParams,
      source: ctaSource
    });
  }, [analytics, analyticsParams]);
  const handleOpen = useCallback((ctaSource_0: LiveLocationCtaSource) => {
    if (isOpen) {
      return;
    }
    setSource(ctaSource_0);
    setIsOpen(true);
    markSeen();
    clearSelectedMarkerInMap();
    analytics.event('game_map_live_location_cta_trigger_click', {
      ...analyticsParams,
      source: ctaSource_0
    });
  }, [analytics, analyticsParams, clearSelectedMarkerInMap, isOpen, markSeen]);
  const handleDownloadClick = useCallback(() => {
    analytics.event('game_map_live_location_cta_download_click', {
      ...analyticsParams,
      source
    });
  }, [analytics, analyticsParams, source]);
  const handleAttentionAnimationShown = useCallback(() => {
    markSeen();
    analytics.event('game_map_live_location_attention_animation_impression', analyticsParams);
  }, [analytics, analyticsParams, markSeen]);
  const handleMarkerHover = useCallback(() => handleHover('mock_pin'), [handleHover]);
  const handleMarkerOpen = useCallback(() => handleOpen('mock_pin'), [handleOpen]);
  const control = useMemo<LiveLocationCtaControl | undefined>(() => isEligible ? {
    attentionKey: suppressionKey,
    attentionSuppressed: isSuppressed,
    open: isOpen,
    onOpen: handleOpen,
    onHover: handleHover,
    onAttentionAnimationShown: handleAttentionAnimationShown
  } : undefined, [handleAttentionAnimationShown, handleHover, handleOpen, isEligible, isOpen, isSuppressed, suppressionKey]);
  return {
    control,
    close,
    handleDownloadClick,
    handleMarkerHover,
    handleMarkerOpen,
    isEligible,
    isOpen,
    showMarker
  };
}